import React, { useState, useEffect } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';
import { X, Filter } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { PartnerAutocomplete } from '@/components/ui/PartnerAutocomplete';

export interface ClassFilters {
  types: string[];
  modalities: string[];
  startDate: Date | null;
  endDate: Date | null;
  statuses: string[];
  techniques: string[];
  categories: string[];
  students: any | null;
}

interface ClassFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: ClassFilters) => void;
  onClearFilters: () => void;
  currentFilters: ClassFilters;
  hasActiveFilters: boolean;
}

export const ClassFilterModal: React.FC<ClassFilterModalProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  onClearFilters,
  currentFilters,
  hasActiveFilters
}) => {
  const { t } = useI18n();
  const [tempFilters, setTempFilters] = useState<ClassFilters>(currentFilters);
  const [showTechniques, setShowTechniques] = useState(false);

  // Class types based on the actual database values
  const classTypes = [
    t('calendar.group_class'),
    t('calendar.private_class')
  ];

  // Class categories - using the same as training categories
  const classCategories = [
    'Training',
    'Competition Prep',
    'Drills',
    'Conditioning',
    'Rolling',
    'Open Mat'
  ];

  // Modalities
  const modalities = ['Gi', 'No-Gi'];

  // Technique categories (same as training filter)
  const techniqueCategories = [
    {
      name: 'Guard Types',
      techniques: [
        'Closed Guard',
        'Open Guard',
        'Half Guard',
        'Butterfly Guard',
        'Worm Guard',
        'Reverse De La Riva Guard',
        '50/50 Guard',
        'Sitting Guard',
        'No-Gi Specific Guards',
      ],
    },
    {
      name: 'Guard Passing',
      techniques: [
        'Pressure Passing',
        'Torque/Leg Drag Passing',
        'Leg Weave Passing',
        'Floating/Smash Passing',
        'Movement-Based Passing',
        'Folding Passes',
        'No-Gi Specific Passes',
      ],
    },
    {
      name: 'Submissions',
      techniques: [
        'Armlocks',
        'Chokes',
        'Leg Locks',
        'Shoulder Locks',
        'Spine/Neck Cranks',
        'No-Gi Specific Submissions',
      ],
    },
    {
      name: 'Sweeps & Takedowns',
      techniques: [
        'Guard Sweeps',
        'Half Guard Sweeps',
        'Butterfly Sweeps',
        'De La Riva Sweeps',
        'Takedowns from Standing',
        'Counter Takedowns',
        'Pulling Guard',
      ],
    },
    {
      name: 'Escapes & Reversals',
      techniques: [
        'Mount Escapes',
        'Back Escapes',
        'Side Control Escapes',
        'North South Escapes',
        'Submission Escapes',
        'Turtle Escapes',
        'Half Guard Escapes',
      ],
    },
  ];

  const statusOptions = [t('calendar.completed'), t('calendar.missed'), t('calendar.scheduled')];

  // Handler functions
  const handleFilterChange = (key: keyof ClassFilters, value: any) => {
    setTempFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleTypeToggle = (type: string) => {
    setTempFilters(prev => ({
      ...prev,
      types: prev.types.includes(type)
        ? prev.types.filter(t => t !== type)
        : [...prev.types, type]
    }));
  };

  const handleModalityToggle = (modality: string) => {
    setTempFilters(prev => ({
      ...prev,
      modalities: prev.modalities.includes(modality)
        ? prev.modalities.filter(m => m !== modality)
        : [...prev.modalities, modality]
    }));
  };

  const handleCategoryToggle = (category: string) => {
    setTempFilters(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const handleStatusToggle = (status: string) => {
    setTempFilters(prev => ({
      ...prev,
      statuses: prev.statuses.includes(status)
        ? prev.statuses.filter(s => s !== status)
        : [...prev.statuses, status]
    }));
  };

  const handleTechniqueToggle = (technique: string) => {
    setTempFilters(prev => ({
      ...prev,
      techniques: prev.techniques.includes(technique)
        ? prev.techniques.filter(t => t !== technique)
        : [...prev.techniques, technique]
    }));
  };

  const handleApply = () => {
    onApplyFilters(tempFilters);
    onClose();
  };

  const handleClear = () => {
    const emptyFilters: ClassFilters = {
      types: [],
      modalities: [],
      startDate: null,
      endDate: null,
      statuses: [],
      techniques: [],
      categories: [],
      students: null
    };
    setTempFilters(emptyFilters);
    onClearFilters();
  };

  const handleClose = () => {
    setTempFilters(currentFilters);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-2xl bg-white rounded-2xl p-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-black">
            Filters
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            Customize your class view by selecting filters
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
          {/* Class Type */}
          <div>
            <label className="block text-sm font-medium mb-3 text-black">
              Class Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              {classTypes.map((type) => (
                <div key={type} className="flex items-center space-x-2">
                  <Checkbox
                    id={`type-${type}`}
                    checked={tempFilters.types.includes(type)}
                    onCheckedChange={() => handleTypeToggle(type)}
                  />
                  <label htmlFor={`type-${type}`} className="text-xs text-black">
                    {type}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Modality */}
          <div>
            <label className="block text-sm font-medium mb-3 text-black">
              Modality
            </label>
            <div className="grid grid-cols-2 gap-2">
              {modalities.map((modality) => (
                <div key={modality} className="flex items-center space-x-2">
                  <Checkbox
                    id={`modality-${modality}`}
                    checked={tempFilters.modalities.includes(modality)}
                    onCheckedChange={() => handleModalityToggle(modality)}
                  />
                  <label htmlFor={`modality-${modality}`} className="text-xs text-black">
                    {modality}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Period */}
          <div>
            <label className="block text-sm font-medium mb-3 text-black">
              Period
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                <Input
                  type="date"
                  value={tempFilters.startDate ? format(tempFilters.startDate, "yyyy-MM-dd") : ""}
                  onChange={(e) => {
                    const date = e.target.value ? new Date(e.target.value) : null;
                    handleFilterChange('startDate', date);
                  }}
                  className="text-black border-black bg-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">End Date</label>
                <Input
                  type="date"
                  value={tempFilters.endDate ? format(tempFilters.endDate, "yyyy-MM-dd") : ""}
                  onChange={(e) => {
                    const date = e.target.value ? new Date(e.target.value) : null;
                    handleFilterChange('endDate', date);
                  }}
                  className="text-black border-black bg-white text-sm"
                />
              </div>
            </div>
          </div>

          {/* Class Category */}
          <div>
            <label className="block text-sm font-medium mb-3 text-black">
              Class Category
            </label>
            <div className="grid grid-cols-2 gap-2">
              {classCategories.map((category) => (
                <div key={category} className="flex items-center space-x-2">
                  <Checkbox
                    id={`category-${category}`}
                    checked={tempFilters.categories.includes(category)}
                    onCheckedChange={() => handleCategoryToggle(category)}
                  />
                  <label htmlFor={`category-${category}`} className="text-xs text-black">
                    {category}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Student */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Student</h3>
            <PartnerAutocomplete
              value={tempFilters.students}
              onChange={(students) => handleFilterChange('students', students)}
              placeholder="Type a student name or username"
              className="text-black border-black"
              containerClassName="bg-white"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium mb-3 text-black">
              Status
            </label>
            <div className="grid grid-cols-2 gap-2">
              {statusOptions.map((status) => (
                <div key={status} className="flex items-center space-x-2">
                  <Checkbox
                    id={`status-${status}`}
                    checked={tempFilters.statuses.includes(status)}
                    onCheckedChange={() => handleStatusToggle(status)}
                  />
                  <label htmlFor={`status-${status}`} className="text-xs text-black">
                    {status}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Techniques */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-black">
                Techniques
              </label>
              <button
                type="button"
                onClick={() => setShowTechniques(!showTechniques)}
                className="text-xs text-[#D72E16] hover:text-[#B8251A] font-medium underline"
              >
                {showTechniques ? 'Hide' : 'Show'} techniques
              </button>
            </div>
            
            {!showTechniques && tempFilters.techniques.length === 0 && (
              <div className="text-center mt-3 mb-3">
                <p className="text-xs font-medium text-gray-600">
                  Filter by techniques used!
                </p>
                <p className="text-xs text-gray-600">
                  You can use them to find specific class sessions.
                </p>
              </div>
            )}

            {showTechniques && (
              <div className="space-y-4">
                {techniqueCategories.map((category) => (
                  <div key={category.name} className="border border-gray-200 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-black mb-2">{category.name}</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {category.techniques.map((technique) => (
                        <div key={technique} className="flex items-center space-x-2">
                          <Checkbox
                            id={`technique-${technique}`}
                            checked={tempFilters.techniques.includes(technique)}
                            onCheckedChange={() => handleTechniqueToggle(technique)}
                          />
                          <label htmlFor={`technique-${technique}`} className="text-xs text-black">
                            {technique}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={handleClear}
            className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full"
          >
            Clear All
          </Button>
          <Button
            onClick={handleApply}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full"
          >
            Apply Filters
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
